// EAIRI — the page every buyer sees first.
//
// The design handoff supplied a full marketing surface for this route and the
// build shipped a headline, a sentence and two buttons. This is the port, under
// four rulings taken during the review:
//
//   * the engine version is read from the constant the server actually runs,
//     not written into the copy — the handoff's page said urce-3.0.0 for the
//     whole of the build that shipped urce-3.1.0;
//   * the prices come from the catalog over the wire, like every other price in
//     the product, so there is no figure in this bundle that could drift from
//     what Stripe charges;
//   * the hero score card is labelled an example and its numbers are REAL
//     engine output for an example profile, generated and guarded by
//     tests/app/example-readout.spec.ts;
//   * the handoff's footer links to the ops console and the go-live acceptance
//     sheet are not ported. They were navigation for a prototype whose whole
//     surface was one file; on a live site they are an invitation.
//
// The handoff's full-bleed bands become deep panels inside the shell's own
// container. Breaking out of it with viewport tricks would put this one page in
// disagreement with every other page in the product.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { EairiClient } from "../lib/api.ts";
import { config } from "../lib/config.ts";
import { accessToken } from "../lib/auth.ts";
import example from "../data/example-readout.json";

interface Sku {
  code: string; name: string; desc: string; price: string; featured: boolean;
}

/** The three the teaser shows, in the order the handoff put them. */
const TEASER = ["ORG_FULL", "PACK_DIRECTION", "FULL_SUITE"];

const DOMAINS = ["A", "B", "C", "D", "E", "F"] as const;

export function Landing() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "fr";
  const [skus, setSkus] = useState<Sku[] | null>(null);

  // The catalog is the only price. When it cannot be reached the section still
  // renders — with its heading and its link to the full grid — rather than
  // showing a number this bundle made up.
  useEffect(() => {
    if (!config.configured) return;
    const client = new EairiClient({ baseUrl: config.functionsUrl, getToken: accessToken });
    let alive = true;
    void client.catalog(lang)
      .then((c) => {
        if (!alive) return;
        const all = (c as { reports?: Sku[] }).reports ?? [];
        setSkus(TEASER.map((code) => all.find((s) => s.code === code)).filter(Boolean) as Sku[]);
      })
      .catch(() => { if (alive) setSkus([]); });
    return () => { alive = false; };
  }, [lang]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 68 }}>

      {/* ------------------------------------------------------------ hero */}
      <section style={{ display: "grid", gap: 40, alignItems: "center",
                        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
        <div>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(2rem, 4.4vw, 3rem)",
                       lineHeight: 1.12, margin: "0 0 20px", textWrap: "balance" }}>
            {t("landing.headline")}
          </h1>
          <p style={{ fontSize: "1.06rem", lineHeight: 1.65, color: "var(--on-surface-2)",
                      maxWidth: "56ch", margin: "0 0 26px" }}>
            {t("landing.lede")}
          </p>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <Link to="/diagnostic" style={primaryCta}>{t("landing.ctaFree")}</Link>
            <Link to="/tarifs" style={secondaryCta}>{t("landing.ctaPricing")}</Link>
          </div>
          <p style={{ margin: "22px 0 0", fontFamily: "var(--font-mono)", fontSize: 11.5,
                      color: "var(--on-surface-3)" }}>
            {t("landing.proof", { engine: example.engineVersion })}
          </p>
        </div>

        <ExampleCard />
      </section>

      {/* ---------------------------------------------------- trust strip */}
      <section aria-label={t("landing.layersKick")}
               style={{ display: "grid", gap: 24, padding: "24px 0",
                        borderTop: "1px solid var(--line-soft)",
                        borderBottom: "1px solid var(--line-soft)",
                        // Four facts: 1 / 2 / 4 across, never 3 + 1 — an
                        // orphan on its own row reads as a mistake (QA, 2026-09-22).
                        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))" }}>
        {[["×2", 1], ["+15", 2], ["1–5", 3], ["FR", 4]].map(([glyph, n]) => (
          <div key={String(n)} style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
            <span aria-hidden="true" style={{ flex: "none", width: 34, height: 34,
                     borderRadius: "var(--r-sm)", background: "var(--ground)",
                     border: "1px solid var(--line-soft)", display: "flex",
                     alignItems: "center", justifyContent: "center", color: "var(--on-surface)",
                     fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 600 }}>
              {glyph}
            </span>
            <span>
              <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: "var(--on-surface)" }}>
                {t(`landing.trust${n}h`)}
              </span>
              <span style={{ display: "block", marginTop: 3, fontSize: 12.5, lineHeight: 1.5,
                             color: "var(--on-surface-3)" }}>
                {t(`landing.trust${n}p`)}
              </span>
            </span>
          </div>
        ))}
      </section>

      {/* ------------------------------------------------ the instrument */}
      <section>
        <p className="kicker">{t("landing.layersKick")}</p>
        <h2 style={sectionTitle}>{t("landing.layersTitle")}</h2>
        <p style={{ ...sectionLead, maxWidth: "68ch" }}>{t("landing.layersLead")}</p>

        <div style={{ marginTop: 28, display: "grid", gap: 20,
                      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
          {[["orgTitle", "orgLead"], ["fnTitle", "fnLead"]].map(([title, lead]) => (
            <div key={title} style={card}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "var(--on-surface)" }}>
                {t(`landing.${title}`)}
              </h3>
              <p style={{ margin: "10px 0 0", fontSize: 13.5, lineHeight: 1.6, color: "var(--on-surface-2)" }}>
                {t(`landing.${lead}`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* -------------------------------------------------- the doctrine */}
      <section style={{ ...panel, padding: "44px 36px" }}>
        <p className="kicker" style={{ color: "var(--on-band-accent)" }}>{t("landing.doctrineKick")}</p>
        <h2 style={{ ...sectionTitle, color: "var(--on-band)" }}>{t("landing.doctrineTitle")}</h2>
        <p style={{ margin: "14px 0 0", fontSize: 14.5, lineHeight: 1.7, color: "var(--on-band-2)",
                    maxWidth: "72ch", textWrap: "pretty" }}>
          {t("landing.doctrineLead")}
        </p>

        <div style={{ marginTop: 22, display: "flex", gap: 18, flexWrap: "wrap",
                      fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--on-band-accent)" }}>
          <span>OCR = min(OWR, {t("landing.fCeiling")})</span>
          <span>FWS<sub style={{ fontSize: 9 }}>{t("landing.fPublished")}</sub> = min(FWS, OWR + 15)</span>
        </div>

        <div style={{ marginTop: 28, display: "grid", gap: 18,
                      gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
          {[["OWR", 1], ["OCR", 2], ["IDS", 3]].map(([tag, n]) => (
            <div key={String(n)} style={{ border: "1px solid var(--band-line)",
                                          borderRadius: "var(--r-md)", padding: 20 }}>
              <span className="kicker" style={{ color: "var(--on-band-accent)" }}>{tag}</span>
              <div style={{ marginTop: 8, fontSize: 15, fontWeight: 700, color: "var(--on-band)" }}>
                {t(`landing.doctrine${n}h`)}
              </div>
              <p style={{ margin: "6px 0 0", fontSize: 13, lineHeight: 1.6, color: "var(--on-band-2)" }}>
                {t(`landing.doctrine${n}p`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------------------------------------- the deliverables */}
      <section>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between",
                      gap: 20, flexWrap: "wrap" }}>
          <div>
            <p className="kicker">{t("landing.delivKick")}</p>
            <h2 style={sectionTitle}>{t("landing.delivTitle")}</h2>
          </div>
          {/* Describing a document and showing one are different promises. The
              specimens exist now, so the section that names them links to them. */}
          <Link to="/livrables" style={{ fontSize: 14, fontWeight: 600, color: "var(--sapphire)" }}>
            {t("landing.delivSee")}
          </Link>
        </div>

        <div style={{ marginTop: 28, display: "grid", gap: 20,
                      gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))" }}>
          {[
            { n: 1, badges: ["PDF"] },
            { n: 2, badges: ["PDF", "PPTX", ".SLIDES"] },
            { n: 3, badges: [t("landing.deliv3badge1"), t("landing.deliv3badge2")] },
          ].map(({ n, badges }) => (
            <div key={n} style={{ ...card, display: "flex", flexDirection: "column", gap: 10 }}>
              <span className="kicker" style={{ color: "var(--on-surface-3)" }}>{t(`landing.deliv${n}kick`)}</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: "var(--on-surface)" }}>
                {t(`landing.deliv${n}h`)}
              </span>
              <span style={{ fontSize: 13, lineHeight: 1.6, color: "var(--on-surface-2)", textWrap: "pretty" }}>
                {t(`landing.deliv${n}p`)}
              </span>
              <span style={{ marginTop: "auto", display: "flex", flexWrap: "wrap", gap: 6 }}>
                {badges.map((b) => (
                  <span key={b} style={{ padding: "3px 8px", borderRadius: "var(--r-xs)",
                                         border: "1px solid var(--line)",
                                         fontFamily: "var(--font-mono)", fontSize: 10,
                                         fontWeight: 600, color: "var(--on-surface)" }}>
                    {b}
                  </span>
                ))}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* --------------------------------------------------- the mandate */}
      <section style={{ display: "flex", gap: 28, flexWrap: "wrap", alignItems: "flex-start",
                        background: "var(--surface-2)", border: "1px solid var(--line)",
                        borderRadius: "var(--r-lg)", padding: 30 }}>
        <div style={{ flex: "1 1 380px" }}>
          <p className="kicker">{t("landing.mandateK")}</p>
          <h2 style={{ ...sectionTitle, fontSize: 26 }}>{t("landing.mandateT")}</h2>
          <p style={{ ...sectionLead, maxWidth: "62ch" }}>{t("landing.mandateP")}</p>
        </div>
        <div style={{ flex: "0 1 320px", display: "flex", flexDirection: "column", gap: 16 }}>
          <blockquote style={{ margin: 0, borderLeft: "3px solid var(--accent)",
                               padding: "6px 0 6px 18px", fontSize: 13.5, lineHeight: 1.6,
                               color: "var(--on-surface)", fontStyle: "italic" }}>
            {t("landing.mandateQ")}
          </blockquote>
          {/* Until 0.7.0 this section made its case and then stopped. A mandate
              is the one thing on the site that cannot be bought from a grid, so
              it is the one thing that needs a door. */}
          <Link to="/contact" style={{ ...secondaryCta, alignSelf: "flex-start", fontSize: 14.5 }}>
            {t("landing.mandateCta")}
          </Link>
        </div>
      </section>

      {/* --------------------------------------------------- price teaser */}
      <section>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between",
                      gap: 20, flexWrap: "wrap" }}>
          <div>
            <p className="kicker">{t("landing.priceKick")}</p>
            <h2 style={sectionTitle}>{t("landing.priceTitle")}</h2>
          </div>
          <Link to="/tarifs" style={{ fontSize: 14, fontWeight: 600, color: "var(--sapphire)" }}>
            {t("landing.priceAll")}
          </Link>
        </div>

        {skus && skus.length > 0 && (
          <div style={{ marginTop: 30, display: "grid", gap: 20,
                        gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))" }}>
            {skus.map((s) => {
              const featured = s.code === "PACK_DIRECTION";
              return (
                <div key={s.code} style={{
                  position: "relative", display: "flex", flexDirection: "column",
                  border: `1px solid ${featured ? "var(--accent)" : "var(--line)"}`,
                  borderTop: `4px solid ${featured ? "var(--accent)" : "var(--line-strong)"}`,
                  borderRadius: "var(--r-md)", background: "var(--surface-1)", padding: 26,
                  boxShadow: featured ? "var(--shadow-raised)" : "var(--shadow-card)",
                }}>
                  {featured && (
                    // Navy on gold, not white on gold: white measured 3.20:1
                    // and failed AA at every button site in the product.
                    <span style={{ position: "absolute", top: -11, left: 20, padding: "3px 9px",
                                   borderRadius: "var(--r-xs)", background: "var(--accent)",
                                   color: "var(--on-accent)", fontFamily: "var(--font-mono)",
                                   fontSize: 9.5, fontWeight: 700, letterSpacing: "0.16em" }}>
                      {t("landing.recommended")}
                    </span>
                  )}
                  <span style={{ fontSize: 15, fontWeight: 700, color: "var(--on-surface)" }}>{s.name}</span>
                  <span style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.55, color: "var(--on-surface-3)" }}>
                    {s.desc}
                  </span>
                  <span className="num" style={{ marginTop: 16, fontSize: 30, fontWeight: 700,
                                                 color: "var(--on-surface)" }}>
                    {s.price}
                  </span>
                  <Link to="/tarifs" style={{
                    marginTop: 18, display: "inline-flex", justifyContent: "center",
                    padding: "11px 16px", borderRadius: "var(--r-sm)", fontSize: 13.5,
                    fontWeight: 600, textDecoration: "none",
                    background: featured ? "var(--band)" : "var(--surface-1)",
                    color: featured ? "var(--on-band)" : "var(--on-surface)",
                    border: "1px solid var(--line-strong)",
                  }}>
                    {t("landing.choose")}
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ----------------------------------------------------- the process */}
      <section style={{ display: "grid", gap: 26, paddingTop: 8,
                        borderTop: "1px solid var(--line-soft)",
                        gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))" }}>
        {[1, 2, 3, 4].map((n) => (
          <div key={n}>
            {/* --rust, not --gold: gold text measures 3.20:1 on white and
                fails AA at every size this page uses it. */}
            <div className="num" style={{ fontFamily: "var(--font-mono)", fontSize: 12,
                                          fontWeight: 600, color: "var(--accent-ink)" }}>
              {String(n).padStart(2, "0")}
            </div>
            <div style={{ marginTop: 8, fontSize: 15, fontWeight: 700, color: "var(--on-surface)" }}>
              {t(`landing.step${n}h`)}
            </div>
            <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.6, color: "var(--on-surface-3)",
                          textWrap: "pretty" }}>
              {t(`landing.step${n}p`)}
            </div>
          </div>
        ))}
      </section>

      {/* -------------------------------------------------------- CTA band */}
      <section style={{ ...panel, padding: "34px 36px", display: "flex", gap: 24,
                        flexWrap: "wrap", alignItems: "center",
                        justifyContent: "space-between" }}>
        <div>
          <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 28,
                       fontWeight: 700, color: "var(--on-band)" }}>
            {t("landing.ctaBandTitle")}
          </h2>
          <p style={{ margin: "10px 0 0", fontSize: 14.5, color: "var(--on-band-2)" }}>
            {t("landing.ctaBandSub")}
          </p>
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Link to="/diagnostic" style={primaryCta}>{t("landing.ctaFree")}</Link>
          <Link to="/contact" style={{ ...secondaryCta, background: "transparent",
                                       color: "var(--on-band)" }}>
            {t("landing.ctaTalk")}
          </Link>
        </div>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------------ */

/** The example readout.
 *
 *  Every number here is what the engine produces for the example profile in
 *  tests/app/example-readout.spec.ts, which fails if this file and the engine
 *  disagree. It is labelled an example in the card itself and again underneath,
 *  because a score card on a landing page is exactly the thing a visitor will
 *  otherwise read as somebody's real result. */
function ExampleCard() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "fr";
  const scores = example.domains as Record<string, number>;
  return (
    <figure style={{ margin: 0 }}>
      <div style={{ background: "var(--surface-1)", border: "1px solid var(--line)",
                    borderRadius: "var(--r-lg)", boxShadow: "var(--shadow-raised)",
                    padding: "22px 22px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <span className="kicker" style={{ color: "var(--on-surface-3)" }}>{t("landing.heroCardKick")}</span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700,
                         padding: "3px 8px", borderRadius: "var(--r-xs)",
                         background: "var(--accent)", color: "var(--on-accent)",
                         letterSpacing: "0.14em" }}>
            {t("landing.exampleBadge")}
          </span>
        </div>

        <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={{ border: "1px solid var(--line-soft)", borderRadius: "var(--r-md)", padding: 14 }}>
            <div className="kicker" style={{ color: "var(--on-surface-3)", fontSize: 9.5 }}>
              {t("landing.weighted")}
            </div>
            <div className="score" style={{ marginTop: 6, fontSize: 27, fontWeight: 700,
                                            color: "var(--on-surface)" }}>
              {example.owr}
              <span style={{ fontSize: 12, fontWeight: 400, color: "var(--on-surface-3)" }}> / 100</span>
            </div>
          </div>
          <div style={{ borderRadius: "var(--r-md)", padding: 14, background: "var(--band)", color: "var(--on-band)" }}>
            <div className="kicker" style={{ color: "var(--on-band-2)", fontSize: 9.5 }}>
              {t("landing.constrained")}
            </div>
            <div className="score" style={{ marginTop: 6, fontSize: 27, fontWeight: 700 }}>
              {example.ocr}
              <span style={{ fontSize: 12, fontWeight: 400, color: "var(--on-band-2)" }}> / 100</span>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          {DOMAINS.map((code) => {
            const value = scores[code] ?? 0;
            const isConstraint = code === example.minDomain;
            return (
              <div key={code} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 14, fontFamily: "var(--font-mono)", fontSize: 10.5,
                               fontWeight: 600, color: "var(--on-surface-2)" }}>
                  {code}
                </span>
                <div style={{ flex: 1, height: 7, borderRadius: 4, background: "var(--ground)",
                              overflow: "hidden" }}>
                  <div style={{ height: "100%", borderRadius: 4,
                                width: `${Math.max(4, Math.round(value))}%`,
                                background: isConstraint ? "var(--alert)" : "var(--sapphire)" }} />
                </div>
                <span className="num" style={{ width: 38, textAlign: "right",
                                               fontFamily: "var(--font-mono)", fontSize: 10.5,
                                               color: "var(--on-surface-2)" }}>
                  {value}
                </span>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line-soft)",
                      fontSize: 12, color: "var(--on-surface-2)" }}>
          {t("landing.constraintLabel", {
            code: example.minDomain,
            name: example.constraintName[lang],
          })}
        </div>
      </div>
      <figcaption style={{ marginTop: 10, fontSize: 12, color: "var(--on-surface-3)", maxWidth: "44ch" }}>
        {t("landing.exampleNote")}
      </figcaption>
    </figure>
  );
}

/* ---------------------------------------------------------------- styles -- */

const primaryCta: React.CSSProperties = {
  background: "var(--accent)", color: "var(--on-accent)", padding: "13px 24px",
  borderRadius: "var(--r-sm)", textDecoration: "none", fontWeight: 600,
};

const secondaryCta: React.CSSProperties = {
  // The accent's own stroke rather than a hairline: beside a solid cyan button
  // on an obsidian ground, --line-strong all but disappeared (QA, 2026-09-22).
  border: "1px solid var(--accent)", color: "var(--on-surface)", padding: "13px 24px",
  borderRadius: "var(--r-sm)", textDecoration: "none", fontWeight: 600,
  background: "var(--surface-1)",
};

const sectionTitle: React.CSSProperties = {
  margin: "8px 0 0", fontFamily: "var(--font-display)", fontSize: 30,
  fontWeight: 700, color: "var(--on-surface)", textWrap: "balance",
};

const sectionLead: React.CSSProperties = {
  margin: "12px 0 0", fontSize: 15, lineHeight: 1.65, color: "var(--on-surface-2)",
  textWrap: "pretty",
};

const card: React.CSSProperties = {
  border: "1px solid var(--line)", borderRadius: "var(--r-md)",
  background: "var(--surface-1)", padding: 24, boxShadow: "var(--shadow-card)",
};

const panel: React.CSSProperties = {
  background: "var(--band-2)", borderRadius: "var(--r-lg)", color: "var(--on-band)",
};
